export const GRANTABLE_TOOL_KEYS = ['Logic', 'Deployment', 'Variant', 'Pricing', 'Projects', 'Organization'];

/** Preview tools stay hidden from customers. Super-admins still see them. */
export const HIDDEN_TOOL_KEYS = ['Variant', 'Pricing'];

/** Temporarily hidden nav pages. Release management lives under Product. */
export const HIDDEN_NAV_PATHS = ['/version-control'];

export function isToolHidden(key, isSuperAdmin = false) {
    if (isSuperAdmin) return false;
    return HIDDEN_TOOL_KEYS.includes(key);
}

export function isNavPathHidden(path) {
    return HIDDEN_NAV_PATHS.includes(path);
}

export function visibleToolKeys(keys, isSuperAdmin = false) {
    return keys.filter(key => !isToolHidden(key, isSuperAdmin));
}

/** Product tools that can appear on the Unified Dashboard. Organization stays off the dashboard. */
export const DASHBOARD_TOOL_KEYS = ['Projects', 'Logic', 'Deployment', 'Variant', 'Pricing'];

/** Used when GET /api/auth/me has no `tools` array (unmigrated backend). */
export const FALLBACK_ENABLED_TOOL_KEYS = ['Logic', 'Deployment', 'Variant', 'Pricing', 'Projects'];

export const tools = {
    Dashboard: {
        label: 'Dashboard',
        icon: 'fa-solid fa-chart-line',
        color: '#8B5CF6',
        navItems: [
            { path: '/', label: 'Overview', icon: 'fa-solid fa-house' }
        ]
    },
    Projects: {
        label: 'Project',
        icon: 'fa-solid fa-diagram-project',
        color: 'var(--accent)',
        navItems: [
            { path: '/projects', label: 'Dashboard', icon: 'fa-solid fa-house' },
            { path: '/projects/logic', label: 'Product', icon: 'fa-solid fa-cube', toolKey: 'Logic' },
            { path: '/projects/deployment', label: 'Release', icon: 'fa-solid fa-cloud-arrow-up', toolKey: 'Deployment' },
            { path: '/projects/variant', label: 'Variant', icon: 'fa-solid fa-folder-tree', toolKey: 'Variant' },
            { path: '/projects/pricing', label: 'Price', icon: 'fa-solid fa-tags', toolKey: 'Pricing' }
        ],
        dashboard: {
            liveFetch: true,
            metrics: [
                {
                    id: 'projects',
                    title: 'Projects',
                    icon: 'fa-solid fa-diagram-project',
                    iconColor: 'var(--accent)',
                    description: 'Projects in this organization'
                }
            ],
            quickActions: [
                { text: 'Manage projects', to: '/projects' }
            ],
            activityTypes: ['project'],
            panel: {
                title: 'Project',
                tag: 'Workspace',
                tagColor: 'var(--accent)',
                icon: 'fa-solid fa-diagram-project',
                iconBgColor: 'var(--accent-glow)',
                description: 'Create and organize the projects that Product, Release, Variant, and Price work inside.',
                liveMetrics: true,
                primaryAction: { text: 'Open projects', to: '/projects', color: 'var(--accent)' }
            }
        }
    },
    Logic: {
        label: 'Product',
        icon: 'fa-solid fa-cube',
        color: 'var(--accent)',
        navItems: [
            { path: '/products', label: 'Family', icon: 'fa-solid fa-boxes-stacked' },
            { path: '/rules', label: 'Rules', icon: 'fa-solid fa-sitemap' },
            { path: '/simulator', label: 'Simulator', icon: 'fa-solid fa-flask-gear' },
            { path: '/version-control', label: 'Release management', icon: 'fa-solid fa-code-branch' }
        ],
        dashboard: {
            liveFetch: true,
            metrics: [
                {
                    id: 'projects',
                    title: 'Ecosystem Projects',
                    icon: 'fa-solid fa-diagram-project',
                    iconColor: 'var(--accent)',
                    description: 'Projects available as the active silo for Product, Release, Variant, and Price'
                },
                {
                    id: 'families',
                    title: 'Families',
                    icon: 'fa-solid fa-boxes-stacked',
                    iconColor: '#10B981'
                },
                {
                    id: 'rulesets',
                    title: 'Rule Tables',
                    icon: 'fa-solid fa-chart-network',
                    iconColor: '#F59E0B'
                }
            ],
            quickActions: [
                { text: 'Go to Rule Editor', to: '/rules' },
                { text: 'Manage Families', to: '/products' }
            ],
            activityTypes: ['project', 'family', 'ruleset'],
            panel: {
                title: 'Product',
                tag: 'Rules',
                tagColor: 'var(--accent)',
                icon: 'fa-solid fa-cube',
                iconBgColor: 'var(--accent-glow)',
                description: 'Design rulesets, map logical dependencies, and test matrix configurations inside the active project.',
                liveMetrics: true,
                primaryAction: { text: 'Open Rule Editor', to: '/rules', color: 'var(--accent)' },
                secondaryAction: { text: 'Families', to: '/products', color: 'var(--text-secondary)' }
            }
        }
    },
    Deployment: {
        label: 'Release',
        icon: 'fa-solid fa-cloud-arrow-up',
        color: 'var(--accent)',
        navItems: [
            { path: '/deploy', label: 'Deployment Dashboard', icon: 'fa-solid fa-table-columns' },
            { path: '/deploy/data', label: 'GUI data', icon: 'fa-solid fa-images' },
            { path: '/deploy/designer', label: 'GUI Designer', icon: 'fa-solid fa-compass-drafting' },
            { path: '/deploy/integration', label: 'Integration & Security', icon: 'fa-solid fa-code' },
            { path: '/deploy/analytics', label: 'Analytics', icon: 'fa-solid fa-chart-line' }
        ],
        dashboard: {
            liveFetch: true,
            metrics: [
                {
                    id: 'deployments',
                    title: 'Deployments',
                    icon: 'fa-solid fa-cloud-arrow-up',
                    iconColor: 'var(--accent)'
                },
                {
                    id: 'activeTokens',
                    title: 'Active tokens',
                    icon: 'fa-solid fa-key',
                    iconColor: '#10B981'
                }
            ],
            quickActions: [
                { text: 'Deployment Dashboard', to: '/deploy' },
                { text: 'GUI Designer', to: '/deploy/designer' }
            ],
            activityTypes: ['deployment'],
            panel: {
                title: 'Release',
                tag: 'Releases',
                tagColor: 'var(--accent)',
                icon: 'fa-solid fa-cloud-arrow-up',
                iconBgColor: 'var(--accent-glow)',
                description: 'Manage deployment tokens, embed settings, GUI designs, and published configurator analytics for each family.',
                liveMetrics: true,
                primaryAction: { text: 'GUI Designer', to: '/deploy/designer', color: 'var(--accent)' },
                secondaryAction: { text: 'Deployment Dashboard', to: '/deploy', color: 'var(--text-secondary)' }
            }
        }
    },
    Variant: {
        label: 'Variant',
        icon: 'fa-solid fa-folder-tree',
        color: '#10B981',
        navItems: [
            { path: '/variant/builder', label: 'Variant Builder', icon: 'fa-solid fa-cubes' },
            { path: '/variant/configurations', label: 'Configurations', icon: 'fa-solid fa-sliders' },
            { path: '/variant/bulk', label: 'Bulk Manager', icon: 'fa-solid fa-file-import' }
        ],
        dashboard: {
            liveFetch: false,
            metrics: [],
            quickActions: [
                { text: 'Variant Builder', to: '/variant/builder' },
                { text: 'Configurations', to: '/variant/configurations' }
            ],
            activityTypes: [],
            panel: {
                title: 'Variant',
                tag: 'Products',
                tagColor: '#10B981',
                icon: 'fa-solid fa-folder-tree',
                iconBgColor: 'rgba(16, 185, 129, 0.1)',
                description: 'Build dynamic variant combination trees, map variations, resolve SKU matrix equations, and manage high-volume configuration models.',
                placeholderMetrics: [
                    { label: 'Variations', value: '1,280 Active' },
                    { label: 'References', value: '8 Models' }
                ],
                primaryAction: { text: 'Variant Builder', to: '/variant/builder', color: '#10B981' },
                secondaryAction: { text: 'Configurations', to: '/variant/configurations', color: 'var(--text-secondary)' }
            }
        }
    },
    Pricing: {
        label: 'Price',
        icon: 'fa-solid fa-tags',
        color: '#F59E0B',
        navItems: [
            { path: '/pricing/books', label: 'Price Books', icon: 'fa-solid fa-book-open' },
            { path: '/pricing/rates', label: 'Rate Cards', icon: 'fa-solid fa-address-card' },
            { path: '/pricing/discounts', label: 'Discount Matrices', icon: 'fa-solid fa-percent' }
        ],
        dashboard: {
            liveFetch: false,
            metrics: [],
            quickActions: [
                { text: 'Price Books', to: '/pricing/books' },
                { text: 'Rate Cards', to: '/pricing/rates' }
            ],
            activityTypes: [],
            panel: {
                title: 'Price',
                tag: 'Prices',
                tagColor: '#F59E0B',
                icon: 'fa-solid fa-tags',
                iconBgColor: 'rgba(245, 158, 11, 0.1)',
                description: 'Configure partner-scoped price books, calculate margins automatically, apply contract multiplier rates, and audit discount overlap sheets.',
                placeholderMetrics: [
                    { label: 'Catalog', value: '4 Price Books' },
                    { label: 'Margins', value: '+4.5% Active' }
                ],
                primaryAction: { text: 'Price Books', to: '/pricing/books', color: '#F59E0B' },
                secondaryAction: { text: 'Rate Cards', to: '/pricing/rates', color: 'var(--text-secondary)' }
            }
        }
    },
    Organization: {
        label: 'Organization',
        icon: 'fa-solid fa-building',
        color: '#64748B',
        navItems: [
            { path: '/organization/members', label: 'Members', icon: 'fa-solid fa-users' },
            { path: '/organization/invitations', label: 'Invitations', icon: 'fa-solid fa-envelope' },
            { path: '/organization/usage', label: 'Usage and billing', icon: 'fa-solid fa-chart-pie' },
            { path: '/organization/settings', label: 'Settings', icon: 'fa-solid fa-sliders' }
        ]
    },
    Admin: {
        label: 'Admin',
        icon: 'fa-solid fa-user-shield',
        color: '#EF4444',
        navItems: [
            { path: '/admin/relationships', label: 'Relationships', icon: 'fa-solid fa-network-wired' },
            { path: '/admin/users', label: 'Users', icon: 'fa-solid fa-users' },
            { path: '/admin/organizations', label: 'Organizations', icon: 'fa-solid fa-building' },
            { path: '/admin/release', label: 'Release', icon: 'fa-solid fa-clock' }
        ]
    }
};

export function toolAccessMap(toolList = []) {
    return Object.fromEntries((toolList || []).map(tool => [tool.key, tool]));
}

/**
 * Identity returns camelCase `{ key, access, isEnabled, isPrimary }`.
 * If `tools` is missing entirely, treat the four product tools as enabled.
 */
export function normalizeToolAccessList(toolsFromMe) {
    if (!Array.isArray(toolsFromMe)) {
        return FALLBACK_ENABLED_TOOL_KEYS.map(key => ({
            key,
            access: 'Edit',
            isEnabled: true,
            isPrimary: key === 'Logic'
        }));
    }
    return toolsFromMe;
}

export function isToolEntitled(accessMap, key, isSuperAdmin) {
    if (key === 'Dashboard') return true;
    if (key === 'Admin') return !!isSuperAdmin;
    if (isSuperAdmin) return true;
    return Boolean(accessMap[key]);
}

/** Dashboard visibility. Header membership uses isToolEntitled instead. */
export function isToolPinned(accessMap, key, isSuperAdmin) {
    if (key === 'Dashboard') return true;
    if (key === 'Admin') return !!isSuperAdmin;
    return Boolean(accessMap[key]?.isEnabled);
}

export function canEditTool(accessMap, key, isSuperAdmin) {
    if (isSuperAdmin) return true;
    return accessMap[key]?.access === 'Edit';
}

export function enabledDashboardToolKeys(accessMap, isSuperAdmin) {
    return visibleToolKeys(DASHBOARD_TOOL_KEYS, isSuperAdmin).filter(key => isToolPinned(accessMap, key, isSuperAdmin));
}

export function matchToolKey(pathname) {
    let bestKey = undefined;
    let bestLength = -1;
    for (const key of Object.keys(tools)) {
        for (const item of tools[key].navItems) {
            const matches = item.path === '/'
                ? pathname === '/'
                : pathname === item.path || pathname.startsWith(`${item.path}/`);
            if (matches && item.path.length > bestLength) {
                bestKey = key;
                bestLength = item.path.length;
            }
        }
    }
    return bestKey;
}

/** Paths that must not stay active for their nested routes. */
const EXACT_NAV_PATHS = new Set(['/projects', '/deploy']);

export function isNavPathActive(pathname, path) {
    if (!path) return false;
    if (path === '/' || EXACT_NAV_PATHS.has(path)) return pathname === path;
    return pathname === path || pathname.startsWith(`${path}/`);
}

/**
 * Top of the sidebar: customer product data.
 * `path` opens the project overview. `childrenFrom` reuses that tool's pages.
 */
const PRODUCT_NAV_SECTION_DEFS = [
    { id: 'projects', toolKey: 'Projects', label: 'Dashboard', icon: 'fa-solid fa-house', path: '/projects' },
    { id: 'logic', toolKey: 'Logic', path: '/projects/logic', childrenFrom: 'Logic' },
    { id: 'deployment', toolKey: 'Deployment', path: '/projects/deployment', childrenFrom: 'Deployment' },
    { id: 'variant', toolKey: 'Variant', path: '/projects/variant', childrenFrom: 'Variant' },
    { id: 'pricing', toolKey: 'Pricing', path: '/projects/pricing', childrenFrom: 'Pricing' }
];

export function productNavSections(isSuperAdmin = false) {
    return PRODUCT_NAV_SECTION_DEFS.filter(section => !isToolHidden(section.toolKey, isSuperAdmin));
}

export const PRODUCT_NAV_SECTIONS = productNavSections(false);

/**
 * Bottom of the sidebar: system data. These sections only expand;
 * their pages are the child links.
 */
export const SYSTEM_NAV_SECTIONS = [
    { id: 'organization', toolKey: 'Organization', childrenFrom: 'Organization' },
    { id: 'admin', toolKey: 'Admin', childrenFrom: 'Admin' }
];

export function resolveNavSection(section) {
    const source = tools[section.childrenFrom || section.toolKey];
    const children = (section.childrenFrom ? (tools[section.childrenFrom]?.navItems || []) : [])
        .filter(child => !isNavPathHidden(child.path));
    return {
        ...section,
        label: section.label || source?.label || section.id,
        icon: section.icon || source?.icon || 'fa-solid fa-circle',
        children,
        expandOnly: !section.path
    };
}

export function sectionMatchesPath(section, pathname) {
    const resolved = resolveNavSection(section);
    if (resolved.path && isNavPathActive(pathname, resolved.path)) return true;
    return resolved.children.some(child => isNavPathActive(pathname, child.path));
}

/** First route the user is allowed to open. Avoids sending them to a path owned by another tool. */
export function firstAccessiblePath(accessMap, isSuperAdmin) {
    for (const section of [...productNavSections(isSuperAdmin), ...SYSTEM_NAV_SECTIONS]) {
        if (!isToolEntitled(accessMap, section.toolKey, isSuperAdmin)) continue;
        const resolved = resolveNavSection(section);
        const candidates = [
            ...(resolved.path ? [resolved.path] : []),
            ...resolved.children.map(child => child.path)
        ];
        for (const path of candidates) {
            const key = matchToolKey(path);
            if (!key || isToolEntitled(accessMap, key, isSuperAdmin)) return path;
        }
    }
    return '/projects';
}
